import * as React from 'react';


/**
 * When your diff function changes outputs, this hook holds on to that value until you reset it.
 * Make sure your diff function handles the case where `prev` is undefined -- that's how this hook's value is initialized
 * When calling reset, you can reset to any value you want, which will be maintained until the diff function returns a
 * new value that doesn't match.
 *
 * Useful for cases like API loading where you want to track when a call has completed successfully.
 * @code
 * ```
 * const [loaded] = useStickyDiff(
 *   (prev, next) => prev?.loading !== next.loading && next.results,
 *   {loading, results},
 *   false,
 * );
 * ```
 * @returns [value, resetFunc]
 */
export const useStickyDiff = <TIn, TOut>(
  diffFn: (prevProps: TIn | undefined, nextProps: TIn) => TOut,
  input: TIn,
  startingValue: TOut | undefined = undefined,
): [TOut | undefined, (newValue: TOut) => void] => {
  const hasChanged = React.useRef<boolean>(false);
  const prevInput  = React.useRef<TIn | undefined>(undefined);
  const returnVal  = React.useRef<TOut | undefined>(startingValue);

  React.useEffect(
    () => {
      const newOut      = diffFn(prevInput.current, input);
      prevInput.current = input;

      // If this is the first change, keep the results
      if (!hasChanged.current && returnVal.current !== newOut) {
        returnVal.current  = newOut;
        hasChanged.current = true;
      }
    },
    [input, diffFn, hasChanged, prevInput, returnVal],
  );

  const setState = React.useCallback(
    (newValue: TOut): TOut => {
      hasChanged.current = false;
      return returnVal.current = newValue;
    },
    [returnVal, hasChanged],
  );

  return [returnVal.current, setState];
};

